/**
 * Reading a number a human typed.
 *
 * Every form in both clients had its own copy of this one-liner, which is one
 * copy per place someone could forget the comma. Half of Europe types "79,5",
 * and a phone keypad set to a German locale offers a comma as the decimal
 * separator — so a naive `Number()` turns a target weight into `NaN` and the
 * form says "that must be a number" about a number.
 *
 * The unicode minus is the same class of problem from the other direction: iOS
 * substitutes U+2212 for a hyphen in some keyboards, and a weekly rate of
 * "−0.5" would otherwise be unreadable.
 */
export function parseDecimal(raw: string | undefined | null): number {
  const text = (raw ?? "").trim();
  // `Number("")` is 0, which is a JavaScript wart and a source of real bugs: a
  // user who *clears* a field has said "I don't know", not "zero". Reading it
  // as zero let an emptied protein box file a correction setting protein to
  // nought on a food shared with every other user of that barcode. Blank is
  // not a number, so it reads as NaN and every caller's existing finiteness
  // check rejects it; the two places that genuinely want a blank to mean zero
  // say so with `|| 0`.
  if (!text) return Number.NaN;
  return Number(text.replace(",", ".").replace("−", "-"));
}

/** True when the input is a real, finite number — not blank, not `NaN`. */
export function isNumber(value: number): boolean {
  return Number.isFinite(value);
}
